// Types for managing multiple concurrent extractions
import { ExtractionProgressData, ExtractionResponse, ExtractionRequest, SocketError } from './icd10';

export enum ExtractionStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export interface ExtractionSession {
  id: string;
  status: ExtractionStatus;
  // Client-generated id; backend provides a request_id later which we map here
  backendRequestId?: string; 
  originalText: string;
  request: ExtractionRequest;
  progress: ExtractionProgressData[];
  results: ExtractionResponse | null;
  errors: SocketError[];
  startTime: string;
  endTime?: string;
  codeColors: Record<string, string>;
  title?: string; // User-defined title or auto-generated preview
  feedback?: string; // Optional user feedback associated with saved extraction
  duration_seconds?: number; // Processing time in seconds from database
  created_at?: number; // Creation timestamp from database
}

export interface MultiExtractionState {
  sessions: Record<string, ExtractionSession>;
  activeSessionId: string | null;
  selectedSessionId: string | null; // For viewing results
}

export interface ExtractionHistoryItem {
  id: string;
  title: string;
  preview: string; // First 100 chars of original text
  codesCount: number;
  completedAt: string;
  processingTime?: number;
}

// Action types for multi-extraction state management
export type MultiExtractionAction =
  | { type: 'START_EXTRACTION'; payload: { id: string; request: ExtractionRequest; originalText: string; title?: string } }
  | { type: 'UPDATE_PROGRESS'; payload: { id: string; progress: ExtractionProgressData } }
  | { type: 'COMPLETE_EXTRACTION'; payload: { id: string; results: ExtractionResponse } }
  | { type: 'ATTACH_BACKEND_ID'; payload: { id: string; backendRequestId: string } }
  | { type: 'ADD_ERROR'; payload: { id: string; error: SocketError } }
  | { type: 'CANCEL_EXTRACTION'; payload: { id: string } }
  | { type: 'SELECT_SESSION'; payload: { id: string | null } }
  | { type: 'DELETE_SESSION'; payload: { id: string } }
  | { type: 'UPDATE_CODE_COLORS'; payload: { id: string; codeColors: Record<string, string> } }
  | { type: 'UPDATE_TITLE'; payload: { id: string; title: string } }
  | { type: 'LOAD_PERSISTED_SESSIONS'; payload: { sessions: Record<string, ExtractionSession> } };